import { ccpProvider } from 'core/CcpProvider'
import { inject } from 'mobx-react'
import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { Dropdown, Icon, Loader } from 'semantic-ui-react'
import styled from 'styled-components'
import Button from './Button'

const AddContainer = styled.div`
  display: flex;
  min-width: 15em;
  .ui.selection.dropdown {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }
  .ui.button {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    margin-right: 0;
  }
`

const AddMapping = ({ agentId, mapping = [], mappingAdded = () => {}, actions }) => {
  const [personalities, setPersonalities] = useState([])
  const [filteredPersonalities, setFilteredPersonalities] = useState([])
  const [selectedPersonality, setSelectedPersonality] = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setLoading(true)
    ccpProvider.api.admin.listPersonalities(0, true)
      .then(([result]) => {
        setLoading(false)
        setPersonalities(result)
      })
  }, [])

  useEffect(() => {
    const personalitiesValues = mapping.reduce((x, { personalityValue }) => [...x, personalityValue], [])
    setFilteredPersonalities(personalities
      .filter(x => !personalitiesValues.includes(x.personalityValue))
      .map(n => ({ key: n.personalityValue, text: n.personalityValue, value: n.personalityValue }))
    )
  }, [personalities, mapping, loading])

  const add = async e => {
    e.preventDefault()
    if(!selectedPersonality) {
      return
    }
    setLoading(true)

    try {
      const [, error] = await actions.admin.assignPersonality(agentId, selectedPersonality)
      if(error) {
        throw error
      }
      const addedPersonality = personalities.find(x => x.personalityValue === selectedPersonality)
      mappingAdded(addedPersonality)
      toast.success(`${addedPersonality.personalityValue} personality assigned to bot agent.`)
      setLoading(false)
    } catch (error) {
      setLoading(false)
      toast.error('There was an issue assigning this personality to bot agent, please try again.')
    }
  }

  return (
    <AddContainer>
      <Dropdown
        placeholder='Select Personality'
        fluid
        selection
        options={filteredPersonalities}
        disabled={loading}
        onChange={(_, { value }) => setSelectedPersonality(value)}
      >
      </Dropdown>
      <Button color="blue" disabled={!selectedPersonality} loading={loading} onClick={add}>Add</Button>
    </AddContainer>
  )
}

const RemoveMapping = ({ personality, agentId, personalityRemoved = () => {}, actions }) => {
  const [loading, setLoading] = useState(false)

  const remove = async e => {
    e.preventDefault()
    setLoading(true)

    try {
      const [, error] = await actions.admin.removePersonality(agentId, personality.personalityValue)
      if(error) {
        throw error
      }
      personalityRemoved(personality.personalityValue)
      setLoading(false)
      toast.success(`${personality.personalityValue} personality removed from the bot agent..`)
    } catch (error) {
      setLoading(false)
      toast.error('There was an issue deleting this personality to bot agent, please try again.')
    }
  }

  return (
    <Button icon onClick={remove} loading={loading}><Icon className="red" name="remove"/></Button>
  )
}

const PersonalityMappingContainer = styled.div`
  position: relative;
  margin: 2em 0;
`
const PersonalityList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 1em 0;
  max-width: 15em;
  li {
    border: solid lightgrey;
    border-width: 1px 1px 0 1px;
    padding: 0.4em 1em;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    &:first-child {
      border-radius: 5px 5px 0 0;
    }
    &:last-child {
      border-radius: 0 0 5px 5px;
      border-width: 1px 1px 1px 1px;
    }
    &:first-child:last-child {
      border-radius: 5px;
    }
  }
`
const Title = () => <h3>Assigned Personalities</h3>
const PersonalityMapping = ({ actions, agentId }) => {
  const [loading, setLoading] = useState(true)
  const [mapping, setMapping] = useState([])

  useEffect(() => {
    const getPersonalitiesAsync = async() => {
      setLoading(true)
      try {
        const [result, error] = await actions.admin.listPersonalities(agentId, false)
        if(error) {
          throw error
        }
        setMapping(result)
        setLoading(false)
      } catch (error) {
        toast.error('There was an error fetching the assigned personalities, please refresh the page to re-fetch them.')
        setLoading(false)
      }
    }
    getPersonalitiesAsync()
  }, [])

  const mappingAdded = (personality) => {
    setMapping([...mapping, personality])
  }

  const personalityRemoved = (personalityValue) => {
    setMapping(mapping.filter(m => m.personalityValue !== personalityValue))
  }

  if(loading) {
    return (
      <PersonalityMappingContainer>
        <Title/>
        <Loader active={true}/>
      </PersonalityMappingContainer>
    )
  }

  return (
    <PersonalityMappingContainer>
      <Title/>
      <PersonalityList>
        {!loading && mapping.length === 0 && <p>No Personalities are assigned to this agent</p>}
        {mapping.map((personality, index) => <li key={index}>{personality.personalityValue}<RemoveMapping personality={personality} personalityRemoved={personalityRemoved} agentId={agentId} actions={actions}/></li>) }
      </PersonalityList>
      <AddMapping mappingAdded={mappingAdded} mapping={mapping} agentId={agentId} actions={actions}/>
    </PersonalityMappingContainer>
  )
}

export default inject('store', 'actions')(PersonalityMapping)

